import { defineContract, listOf, nullableString, timestamp, z } from "./_base";

/*
 * AI engine, approvals inbox, system health — contracts for the operator
 * surfaces. Verified against routes/ai.ts, routes/ai-config.ts,
 * routes/info-updates.ts, routes/whatsapp.ts and the ai_audit_log table.
 *
 * HONESTY NOTE for the "show me exactly what the AI saw" requirement: the audit
 * table stores `input_preview` / `output_preview` (truncated) and a
 * `prompt_hash`, NOT the full prompt. The UI can show the preview and prove
 * integrity via the hash; showing the complete prompt needs a backend change
 * (persist the full input, or a per-entry blob). Stated here so nobody reads a
 * preview as the whole.
 */

/* ── Audit log: every model call ─────────────────────────────────────── */
export const auditEntrySchema = z.object({
  id: z.string(),
  task: z.string(),                       /* chat | profile | score | summary | … */
  provider: nullableString,
  model: nullableString,
  prompt_hash: nullableString,
  input_preview: nullableString,
  output_preview: nullableString,
  tokens_in: z.number().nullish(),
  tokens_out: z.number().nullish(),
  cost: z.union([z.number(), z.string()]).nullish().transform((v) => (v == null ? undefined : Number(v))),
  review_quality: nullableString,
  review_notes: nullableString,
  entity_id: nullableString,
  created_at: timestamp,
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

export const auditLogContract = defineContract({
  name: "ai.audit-log",
  method: "GET",
  path: () => "/ai/audit-log",
  response: z.object({
    entries: z.array(auditEntrySchema).optional().default([]),
    limit: z.number().optional(),
    offset: z.number().optional(),
  }),
  params: (q: { limit?: number; offset?: number; task?: string }) => ({
    limit: String(Math.min(q.limit ?? 50, 200)),
    offset: String(q.offset ?? 0),
    ...(q.task ? { task: q.task } : {}),
  }),
});

export const auditReviewContract = defineContract({
  name: "ai.audit-log.review",
  method: "POST",
  path: (i: { id: string; quality: string; notes?: string }) => `/ai/audit-log/${i.id}/review`,
  response: z.unknown(),
  params: ({ quality, notes }) => ({ quality, ...(notes ? { notes } : {}) }),
});

/* ── Approvals inbox: AI-proposed field changes ──────────────────────── */
export const infoUpdateSchema = z.object({
  id: z.string(),
  entityId: z.string(),
  source: nullableString,                 /* whatsapp | email | ai | … */
  field: z.string(),
  oldValue: nullableString,
  newValue: nullableString,
  reason: nullableString,
  status: z.string().optional().default("pending"),
  confidence: z.number().nullish(),
  metadata: z.record(z.unknown()).nullish().transform((v) => v ?? {}),
  createdAt: timestamp,
  entity: z.object({ id: z.string(), displayName: z.string() }).nullish(),
});
export type InfoUpdate = z.infer<typeof infoUpdateSchema>;

export const infoUpdatesContract = defineContract({
  name: "info-updates.list",
  method: "GET",
  path: () => "/info-updates",
  response: listOf(infoUpdateSchema),
  params: (q: { status?: string; limit?: number }) => ({
    ...(q.status ? { status: q.status } : {}),
    ...(q.limit ? { limit: String(q.limit) } : {}),
  }),
});

export const approveInfoUpdateContract = defineContract({
  name: "info-updates.approve",
  method: "POST",
  path: (i: { id: string }) => `/info-updates/${i.id}/approve`,
  response: z.unknown(),
});
export const rejectInfoUpdateContract = defineContract({
  name: "info-updates.reject",
  method: "POST",
  path: (i: { id: string; reason?: string }) => `/info-updates/${i.id}/reject`,
  response: z.unknown(),
  params: ({ reason }) => (reason ? { reason } : {}),
});

/* ── Engine: providers, personas/skills/guardrails, WhatsApp session ──── */
export const aiConfigItemSchema = z.object({
  id: z.string().optional(),
  slug: z.string().optional(),
  name: z.string().optional(),
  description: nullableString,
  enabled: z.boolean().optional(),
}).passthrough();

export const personasContract = defineContract({ name: "ai-config.personas", method: "GET", path: () => "/ai-config/personas", response: listOf(aiConfigItemSchema) });
export const skillsContract = defineContract({ name: "ai-config.skills", method: "GET", path: () => "/ai-config/skills", response: listOf(aiConfigItemSchema) });
export const guardrailsContract = defineContract({ name: "ai-config.guardrails", method: "GET", path: () => "/ai-config/guardrails-config", response: listOf(aiConfigItemSchema) });

export const waStatusContract = defineContract({
  name: "whatsapp.status",
  method: "GET",
  path: () => "/whatsapp/status",
  response: z.object({
    connected: z.boolean().optional().default(false),
    status: nullableString,
    engine: nullableString,
    phone: nullableString,
    lastMessageAt: timestamp,
  }).passthrough(),
});

/** Aggregate today's economics from the audit log client-side — the API has
 *  no rollup endpoint for calls/tokens/cost, and the founder frame wants one. */
export function summarizeAudit(entries: AuditEntry[]) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const t = entries.filter((e) => e.created_at && e.created_at >= today);
  const sum = (xs: AuditEntry[], f: (e: AuditEntry) => number | undefined) => xs.reduce((a, e) => a + (f(e) ?? 0), 0);
  const byModel = new Map<string, number>();
  for (const e of t) byModel.set(e.model ?? "?", (byModel.get(e.model ?? "?") ?? 0) + 1);
  return {
    calls: t.length,
    tokensIn: sum(t, (e) => e.tokens_in ?? undefined),
    tokensOut: sum(t, (e) => e.tokens_out ?? undefined),
    cost: sum(t, (e) => e.cost),
    byModel: [...byModel.entries()].sort((a, b) => b[1] - a[1]),
    /* True only when every call's provider is the in-cluster one. */
    localShare: t.length ? t.filter((e) => (e.provider ?? "").match(/ollama|litellm|local/i)).length / t.length : 0,
  };
}
